import React, { useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import LandingPage from './pages/LandingPage';
import AdminGuide from './pages/AdminGuide';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Batches from './pages/Batches';
import UserAnalytics from './pages/UserAnalytics';
import Gyms from './pages/Gyms';
import GymDetail from './pages/GymDetail';
import Exercises from './pages/Exercises';
import Goals from './pages/Goals';
import Notifications from './pages/Notifications';
import TrainerPlans from './pages/TrainerPlans';
import Trainers from './pages/Trainers';
import WorkoutPlans from './pages/WorkoutPlans';
import MealPlans from './pages/MealPlans';
import StaffManagement from './pages/StaffManagement';
import Attendance from './pages/Attendance';
import FoodItems from './pages/FoodItems';
import Feedbacks from './pages/Feedbacks';
import DatabaseManagement from './pages/DatabaseManagement';
import AuditLog from './pages/AuditLog';
import BodyMeasurements from './pages/BodyMeasurements';
import Payments from './pages/Payments';
import GymUpiPayments from './pages/GymUpiPayments';
import Invoices from './pages/Invoices';
import GymInvoices from './pages/GymInvoices';
import InvoiceSettings from './pages/InvoiceSettings';
import GymSettings from './pages/GymSettings';
import Integrations from './pages/Integrations';
import SlotManagement from './pages/SlotManagement';
import Retention from './pages/Retention';
import Leads from './pages/Leads';
import StressContent from './pages/StressContent';
import YogaContent from './pages/YogaContent';
import Marketing from './pages/Marketing';
import NotificationTemplates from './pages/NotificationTemplates';
import Banners from './pages/Banners';
import ChallengesEvents from './pages/ChallengesEvents';
import POS from './pages/POS';
import CheckInKiosk from './pages/CheckInKiosk';
import SocialOperations from './pages/SocialOperations';
import BiometricIntegration from './pages/BiometricIntegration';


import Layout from './components/Layout';
import RequireRole from './components/RequireRole';
import { AppDataProvider, useAppData } from './context/AppDataContext';
import AccessibilityProvider from './components/AccessibilityProvider';
import { GYM_MODULES, userCanAccessGymModule } from './utils/gymModules';

import { ColorModeContext } from './context/ThemeContext';

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
};

const RequireGymModule = ({ moduleKey, children }) => {
  const { gyms, gymsLoading } = useAppData();
  const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();

  if (user?.role === 'superadmin' || !moduleKey) return children;
  if (gymsLoading) return null;
  return userCanAccessGymModule(user, gyms, moduleKey) ? children : <Navigate to="/dashboard" replace />;
};

const RequireDefaultGymAdmin = ({ children }) => {
  const { gyms, gymsLoading } = useAppData();
  const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();

  if (user?.role === 'superadmin') return children;
  if (gymsLoading) return null;

  const userGymId = typeof user?.gymId === 'string' ? user.gymId : (user?.gymId?._id || user?.gymId?.id || '');
  const defaultGym = gyms.find(gym => gym.isDefault);
  const defaultGymId = defaultGym?._id || defaultGym?.id || '';
  return user?.role === 'admin' && userGymId && userGymId === defaultGymId
    ? children
    : <Navigate to="/dashboard" replace />;
};

function App() {
  const [mode, setMode] = useState('light');
  
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
      mode,
    }),
    [mode],
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === 'light' ? '#7C3AED' : '#8B5CF6',
            light: mode === 'light' ? '#A78BFA' : '#C4B5FD',
            dark: mode === 'light' ? '#6D28D9' : '#7C3AED',
            contrastText: '#ffffff',
          },
          secondary: {
            main: mode === 'light' ? '#24292f' : '#c9d1d9',
            light: mode === 'light' ? '#57606a' : '#8b949e',
            dark: mode === 'light' ? '#0f1419' : '#b1bac4',
            contrastText: mode === 'light' ? '#ffffff' : '#0d1117',
          },
          background: {
            default: mode === 'light' ? '#f6f8fa' : '#0d1117',
            paper: mode === 'light' ? '#ffffff' : '#161b22',
          },
          text: {
            primary: mode === 'light' ? '#24292f' : '#c9d1d9',
            secondary: mode === 'light' ? '#57606a' : '#8b949e',
            disabled: mode === 'light' ? '#8c959f' : '#484f58',
          },
          divider: mode === 'light' ? '#d0d7de' : '#30363d',
          error:   { main: mode === 'light' ? '#cf222e' : '#f85149', light: mode === 'light' ? '#FFEBE9' : '#490202' },
          success: { main: mode === 'light' ? '#1a7f37' : '#238636', light: mode === 'light' ? '#dafbe1' : '#04260f', dark: mode === 'light' ? '#116329' : '#2ea043' },
          warning: { main: mode === 'light' ? '#9a6700' : '#d29922', light: mode === 'light' ? '#fff8c5' : '#4d3200' },
          info:    { main: mode === 'light' ? '#7C3AED' : '#8B5CF6', light: mode === 'light' ? '#F3E8FF' : '#3B0764' },
        },
        typography: {
          fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif',
          fontSize: 14,
          h1: { fontWeight: 600, fontSize: '2.5rem', letterSpacing: '-0.02em', lineHeight: 1.25 },
          h2: { fontWeight: 600, fontSize: '2rem', letterSpacing: '-0.01em', lineHeight: 1.25 },
          h3: { fontWeight: 600, fontSize: '1.75rem', lineHeight: 1.25 },
          h4: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.25 },
          h5: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.25 },
          h6: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.25 },
          body1: { fontSize: '0.875rem', lineHeight: 1.5, color: mode === 'light' ? '#24292f' : '#c9d1d9' },
          body2: { fontSize: '0.75rem',  lineHeight: 1.5, color: mode === 'light' ? '#57606a' : '#8b949e' },
          caption: { fontSize: '0.75rem', lineHeight: 1.5, color: mode === 'light' ? '#57606a' : '#8b949e' },
          button: { textTransform: 'none', fontWeight: 500, fontSize: '0.875rem' },
        },
        shape: { borderRadius: 6 },
        shadows: [
          'none',
          '0 1px 0 rgba(27,31,36,0.04)',
          '0 1px 3px rgba(27,31,36,0.12), 0 1px 2px rgba(27,31,36,0.08)',
          '0 3px 6px rgba(27,31,36,0.15)',
          '0 4px 8px rgba(27,31,36,0.2)',
          '0 8px 24px rgba(27,31,36,0.12)',
          '0 12px 28px rgba(27,31,36,0.15)',
          ...Array(18).fill('none'),
        ],
        components: {
          MuiButton: {
            defaultProps: { disableElevation: true, disableRipple: true },
            styleOverrides: {
              root: {
                borderRadius: 6,
                padding: '5px 16px',
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'none',
                minHeight: 32,
                transition: '80ms cubic-bezier(0.33, 1, 0.68, 1)',
                '&:focus-visible': {
                  outline: `2px solid ${mode === 'light' ? '#7C3AED' : '#8B5CF6'}`,
                  outlineOffset: 2,
                },
              },
              contained: {
                boxShadow: '0 1px 0 rgba(27,31,36,0.04)',
                '&:hover': { transform: 'none' },
                '&:active': { transform: 'none' },
              },
              containedPrimary: {
                background: mode === 'light' ? '#7C3AED' : '#8B5CF6',
                border: `1px solid ${mode === 'light' ? 'rgba(27,31,36,0.15)' : 'rgba(240,246,252,0.1)'}`,
                boxShadow: '0 1px 0 rgba(27,31,36,0.1), inset 0 1px 0 rgba(255,255,255,0.03)',
                '&:hover': {
                  background: mode === 'light' ? '#6D28D9' : '#7C3AED',
                  boxShadow: '0 1px 0 rgba(27,31,36,0.1), inset 0 1px 0 rgba(255,255,255,0.03)',
                }
              },
              outlined: {
                borderWidth: '1px',
                borderColor: mode === 'light' ? 'rgba(27,31,36,0.15)' : 'rgba(240,246,252,0.1)',
                backgroundColor: mode === 'light' ? '#f6f8fa' : '#21262d',
                boxShadow: '0 1px 0 rgba(27,31,36,0.04), inset 0 1px 0 rgba(255,255,255,0.25)',
                color: mode === 'light' ? '#24292f' : '#c9d1d9',
                '&:hover': { 
                  backgroundColor: mode === 'light' ? '#f3f4f6' : '#30363d',
                  borderColor: mode === 'light' ? 'rgba(27,31,36,0.15)' : '#8b949e',
                  transform: 'none',
                  boxShadow: '0 1px 0 rgba(27,31,36,0.04), inset 0 1px 0 rgba(255,255,255,0.25)',
                },
              },
              sizeSmall: { padding: '3px 12px', fontSize: '0.75rem', minHeight: 28 },
              sizeLarge: { padding: '9px 20px', fontSize: '1rem', minHeight: 40, borderRadius: 6 },
            },
          },
          MuiAppBar: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
              root: ({ theme }) => ({
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }),
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: ({ theme }) => ({
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                borderColor: theme.palette.divider,
              }),
            },
          },
          MuiCard: {
            defaultProps: {
              variant: 'outlined',
            },
            styleOverrides: {
              root: ({ theme }) => ({
                borderRadius: 12,
                borderColor: theme.palette.divider,
                boxShadow: '0 4px 24px rgba(0,0,0,0.02)',
              }),
            },
          },
          MuiPaper: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
              root: ({ theme }) => ({
                backgroundImage: 'none',
                border: `1px solid ${theme.palette.divider}`,
              }),
              elevation1: {
                boxShadow: '0 1px 3px rgba(27,31,36,0.12), 0 1px 2px rgba(27,31,36,0.08)',
              }
            },
          },
          MuiCssBaseline: {
            styleOverrides: (themeParam) => ({
              html: { 
                scrollBehavior: 'smooth',
                colorScheme: mode,
              },
              body: {
                backgroundColor: themeParam.palette.background.default,
                color: themeParam.palette.text.primary,
                fontFamily: themeParam.typography.fontFamily,
              },
              '*': { boxSizing: 'border-box' },
              '::-webkit-scrollbar': { width: 8, height: 8 },
              '::-webkit-scrollbar-thumb': {
                backgroundColor: mode === 'light' ? '#d0d7de' : '#484f58',
                borderRadius: 4,
                border: `2px solid ${themeParam.palette.background.default}`,
                '&:hover': { backgroundColor: mode === 'light' ? '#8c959f' : '#8b949e' }
              },
              '::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            }),
          },
          MuiCard: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
              root: ({ theme }) => ({
                borderRadius: 6,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                boxShadow: 'none',
              }),
            },
          },
          MuiCardContent: {
            styleOverrides: {
              root: { padding: 16, '&:last-child': { paddingBottom: 16 } },
            },
          },
          MuiTableContainer: {
            styleOverrides: {
              root: ({ theme }) => ({
                borderRadius: 6,
                border: `1px solid ${theme.palette.divider}`,
                overflowX: 'auto',
                backgroundColor: theme.palette.background.paper,
              }),
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: ({ theme }) => ({
                borderBottom: `1px solid ${theme.palette.divider}`,
                padding: '12px 16px',
                fontSize: '0.875rem',
                color: theme.palette.text.primary,
              }),
              head: ({ theme }) => ({
                fontWeight: 600,
                fontSize: '0.875rem',
                color: theme.palette.text.primary,
                backgroundColor: mode === 'light' ? '#f6f8fa' : '#161b22',
                padding: '12px 16px',
                borderBottom: `1px solid ${theme.palette.divider}`,
                textTransform: 'none',
                letterSpacing: 'normal',
              }),
            },
          },
          MuiTableRow: {
            defaultProps: {
              hover: true,
            },
            styleOverrides: {
              root: ({ theme }) => ({
                '&:last-child td': { borderBottom: 0 },
                transition: 'background-color 0.15s ease',
              }),
            },
          },
          MuiChip: {
            styleOverrides: {
              root: ({ theme, ownerState }) => ({
                fontWeight: 600,
                fontSize: '0.75rem',
                borderRadius: 12,
                height: 24,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& .MuiChip-label': {
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                  px: 1,
                },
                ...((!ownerState.color || ownerState.color === 'default') && ownerState.variant !== 'outlined' && {
                  backgroundColor: mode === 'light' ? '#F3F4F6' : '#21262d',
                  color: mode === 'light' ? '#374151' : '#c9d1d9',
                  border: `1px solid ${mode === 'light' ? '#D1D5DB' : '#30363d'}`,
                }),
                ...((!ownerState.color || ownerState.color === 'default') && ownerState.variant === 'outlined' && {
                  backgroundColor: 'transparent',
                  color: theme.palette.text.primary,
                  border: `1px solid ${theme.palette.divider}`,
                }),
                ...(ownerState.color === 'success' && {
                  backgroundColor: mode === 'light' ? '#DCFCE7' : '#04260f',
                  color: mode === 'light' ? '#15803D' : '#4ade80',
                  border: `1px solid ${mode === 'light' ? '#86EFAC' : '#166534'}`,
                }),
                ...(ownerState.color === 'warning' && {
                  backgroundColor: mode === 'light' ? '#FEF3C7' : '#321c00',
                  color: mode === 'light' ? '#B45309' : '#fde047',
                  border: `1px solid ${mode === 'light' ? '#FDE68A' : '#78350f'}`,
                }),
                ...(ownerState.color === 'error' && {
                  backgroundColor: mode === 'light' ? '#FEE2E2' : '#3c0d0d',
                  color: mode === 'light' ? '#B91C1C' : '#fca5a5',
                  border: `1px solid ${mode === 'light' ? '#FCA5A5' : '#991b1b'}`,
                }),
                ...(ownerState.color === 'primary' && {
                  backgroundColor: mode === 'light' ? '#F3E8FF' : '#2e1065',
                  color: mode === 'light' ? '#6D28D9' : '#c084fc',
                  border: `1px solid ${mode === 'light' ? '#DDD6FE' : '#5b21b6'}`,
                }),
              }),
              sizeSmall: { height: 22, fontSize: '0.6875rem', borderRadius: 10 },
            },
          },
          MuiTextField: {
            defaultProps: { size: 'small', InputLabelProps: { shrink: true }, autoComplete: 'off' },
            styleOverrides: {
              root: ({ theme }) => ({
                '& .MuiOutlinedInput-root': {
                  borderRadius: 6,
                  fontSize: '0.875rem',
                  backgroundColor: theme.palette.background.paper,
                  transition: '80ms cubic-bezier(0.33, 1, 0.68, 1)',
                  boxShadow: 'inset 0 1px 0 rgba(208,215,222,0.2)',
                  marginTop: 0,
                  '& fieldset': { borderColor: theme.palette.divider, top: 0 },
                  '& fieldset legend': { display: 'none' },
                  '&:hover fieldset': { borderColor: theme.palette.divider },
                  '&.Mui-focused fieldset': { 
                    borderColor: theme.palette.primary.main, 
                    borderWidth: '1px' 
                  },
                  '&.Mui-focused': { 
                    boxShadow: `0 0 0 3px ${mode === 'light' ? 'rgba(9,105,218,0.3)' : 'rgba(47,129,247,0.3)'}`,
                  },
                },
                '& .MuiInputLabel-root': { 
                  fontSize: '0.875rem', 
                  color: theme.palette.text.primary,
                  position: 'relative',
                  transform: 'none',
                  fontWeight: 600,
                  marginBottom: '6px',
                },
                '& .MuiFormHelperText-root': { fontSize: '0.75rem', marginTop: 4 },
              }),
            },
          },
          MuiSelect: {
            defaultProps: { size: 'small' },
            styleOverrides: {
              root: { borderRadius: 6, fontSize: '0.875rem' },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: ({ theme }) => ({
                borderRadius: 6,
                backgroundColor: theme.palette.background.paper,
                boxShadow: 'inset 0 1px 0 rgba(208,215,222,0.2)',
                marginTop: 0,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider, top: 0 },
                '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                '&.Mui-focused': { 
                  boxShadow: `0 0 0 3px ${mode === 'light' ? 'rgba(9,105,218,0.3)' : 'rgba(47,129,247,0.3)'}`,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 1,
                },
              }),
            },
          },
          MuiInputLabel: {
            defaultProps: { shrink: true },
            styleOverrides: {
              root: ({ theme }) => ({
                position: 'relative',
                transform: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: theme.palette.text.primary,
                marginBottom: '6px',
              }),
            },
          },
          MuiInputBase: {
            styleOverrides: {
              root: { fontSize: '0.875rem' },
              input: ({ theme }) => ({
                '&:-webkit-autofill': {
                  WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
                  WebkitTextFillColor: `${theme.palette.text.primary} !important`,
                  caretColor: theme.palette.text.primary,
                  borderRadius: 'inherit',
                },
                colorScheme: mode === 'dark' ? 'dark' : 'light',
              }),
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              root: ({ theme }) => ({
                color: theme.palette.text.secondary,
                fontWeight: 500,
                '&.Mui-focused': { color: theme.palette.primary.main },
              }),
            },
          },
          MuiInputAdornment: {
            styleOverrides: {
              root: ({ theme }) => ({
                color: theme.palette.text.primary,
                '& .MuiTypography-root': {
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                },
              }),
            },
          },
          MuiMenu: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
              paper: ({ theme }) => ({
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 12,
                boxShadow: '0 8px 24px rgba(140,149,159,0.2)',
                padding: '4px',
              }),
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: ({ theme }) => ({
                borderRadius: 6,
                margin: '2px 4px',
                minHeight: 32,
                fontSize: '0.875rem',
                color: theme.palette.text.primary,
                '&:hover': { backgroundColor: mode === 'light' ? '#f3f4f6' : '#30363d' },
                '&.Mui-selected': {
                  backgroundColor: mode === 'light' ? '#7C3AED' : '#8B5CF6',
                  color: '#ffffff',
                  fontWeight: 500,
                  '&:hover': { backgroundColor: mode === 'light' ? '#6D28D9' : '#7C3AED' },
                },
              }),
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: ({ theme }) => ({
                borderRadius: 12,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 8px 24px rgba(140,149,159,0.2)',
              }),
            },
          },
          MuiDialogTitle: {
            styleOverrides: {
              root: ({ theme }) => ({
                fontSize: '1rem',
                fontWeight: 600,
                padding: '16px 24px',
                color: theme.palette.text.primary,
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.default,
              }),
            },
          },
          MuiDialogContent: {
            styleOverrides: {
              root: { padding: '16px 24px' },
              dividers: ({ theme }) => ({ borderColor: theme.palette.divider, padding: '24px' }),
            },
          },
          MuiDialogActions: {
            styleOverrides: {
              root: ({ theme }) => ({
                padding: '16px 24px',
                borderTop: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.default,
                gap: 8,
              }),
            },
          },
          MuiAvatar: {
            styleOverrides: {
              root: { fontWeight: 500, fontSize: '0.875rem', borderRadius: '50%' },
            },
          },
          MuiAlert: {
            styleOverrides: {
              root: { borderRadius: 6, fontSize: '0.875rem', alignItems: 'center' },
              standardSuccess: { backgroundColor: mode === 'light' ? '#dafbe1' : '#04260f', color: mode === 'light' ? '#1a7f37' : '#238636', border: `1px solid ${mode === 'light' ? '#4ac26b' : '#2ea043'}` },
              standardError:   { backgroundColor: mode === 'light' ? '#FFEBE9' : '#490202', color: mode === 'light' ? '#cf222e' : '#f85149', border: `1px solid ${mode === 'light' ? '#ff8182' : '#da3633'}` },
              standardWarning: { backgroundColor: mode === 'light' ? '#fff8c5' : '#4d3200', color: mode === 'light' ? '#9a6700' : '#d29922', border: `1px solid ${mode === 'light' ? '#d4a72c' : '#9e6a03'}` },
              standardInfo:    { backgroundColor: mode === 'light' ? '#F3E8FF' : '#3B0764', color: mode === 'light' ? '#7C3AED' : '#8B5CF6', border: `1px solid ${mode === 'light' ? '#C4B5FD' : '#7C3AED'}` },
            },
          },
          MuiDivider: {
            styleOverrides: { root: ({ theme }) => ({ borderColor: theme.palette.divider }) },
          },
          MuiListItemButton: {
            styleOverrides: {
              root: ({ theme }) => ({
                borderRadius: 6,
                transition: 'all 0.1s',
                '&.Mui-selected': { 
                  backgroundColor: mode === 'light' ? '#F3E8FF' : '#3B0764', 
                  color: mode === 'light' ? '#7C3AED' : '#8B5CF6',
                  fontWeight: 500
                },
                '&.Mui-selected:hover': { backgroundColor: mode === 'light' ? '#E9D5FF' : '#581C87' },
              }),
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: '0.75rem',
                borderRadius: 6,
                backgroundColor: mode === 'light' ? '#24292f' : '#6e7681',
                color: '#ffffff',
                padding: '6px 12px',
                fontWeight: 500,
              },
              arrow: { color: mode === 'light' ? '#24292f' : '#6e7681' },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                borderRadius: 6,
                transition: 'background-color 0.2s',
                color: mode === 'light' ? '#57606a' : '#8b949e',
                '&:hover': { backgroundColor: mode === 'light' ? '#f3f4f6' : '#30363d', color: mode === 'light' ? '#24292f' : '#c9d1d9' },
                '&:focus-visible': {
                  outline: `2px solid ${mode === 'light' ? '#7C3AED' : '#8B5CF6'}`,
                  outlineOffset: 2,
                },
              },
            },
          },
          MuiSkeleton: {
            defaultProps: { animation: 'wave' },
            styleOverrides: {
              root: { borderRadius: 6, backgroundColor: mode === 'light' ? '#eaeef2' : '#21262d' },
            },
          },
          MuiPagination: {
            styleOverrides: {
              root: { '& .MuiPaginationItem-root': { borderRadius: 6, fontWeight: 500 } },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: ({ theme }) => ({
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                minHeight: 48,
                color: mode === 'light' ? '#57606a' : '#8b949e',
                '&.Mui-selected': { color: theme.palette.text.primary }
              }),
            },
          },
          MuiTabs: {
            styleOverrides: {
              indicator: { height: 2, borderRadius: 2, backgroundColor: '#fd8c73' },
            },
          },
          MuiLinearProgress: {
            styleOverrides: {
              root: { height: 6, borderRadius: 3, backgroundColor: mode === 'light' ? '#eaeef2' : '#21262d' },
              bar: { borderRadius: 3, backgroundColor: '#2da44e' },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppDataProvider>
      <AccessibilityProvider>
      <BrowserRouter>
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/admin-guide" element={<AdminGuide />} />
            <Route path="/kiosk" element={
              <PrivateRoute>
                <RequireRole roles={['superadmin', 'gym_admin', 'admin', 'manager', 'front_desk']}>
                  <CheckInKiosk />
                </RequireRole>
              </PrivateRoute>
            } />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="users" element={<RequireRole roles={['superadmin', 'gym_admin', 'admin', 'trainer', 'manager', 'coach', 'sales_staff', 'front_desk']}><Users /></RequireRole>} />
              <Route path="batches" element={<RequireRole roles={['superadmin', 'gym_admin', 'admin', 'trainer', 'manager', 'coach']}><Batches /></RequireRole>} />
              <Route path="users/:userId/analytics" element={<RequireRole roles={['superadmin', 'gym_admin', 'admin', 'trainer', 'manager', 'coach']}><UserAnalytics /></RequireRole>} />
              <Route path="users/:userId/measurements" element={<RequireRole roles={['superadmin', 'gym_admin', 'admin', 'trainer', 'manager', 'coach']}><BodyMeasurements /></RequireRole>} />
              <Route path="staff" element={<RequireRole roles={['superadmin', 'admin', 'gym_admin']}><StaffManagement /></RequireRole>} />
              <Route path="marketing" element={<RequireRole roles={['superadmin', 'gym_admin', 'admin', 'manager', 'sales_staff', 'trainer', 'coach']}><Marketing /></RequireRole>} />
              <Route path="banners" element={<RequireRole roles={['superadmin', 'gym_admin', 'admin', 'manager']}><Banners /></RequireRole>} />
              <Route path="gyms" element={<RequireRole roles={['superadmin']}><Gyms /></RequireRole>} />
              <Route path="gyms/:id" element={<RequireRole roles={['superadmin', 'gym_admin']}><GymDetail /></RequireRole>} />
              <Route path="exercises" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager']}><Exercises /></RequireRole>} />
              <Route path="workout-plans" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager']}><WorkoutPlans /></RequireRole>} />
              <Route path="meal-plans" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager']}><MealPlans /></RequireRole>} />
              <Route path="food-items" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager']}><FoodItems /></RequireRole>} />
              <Route path="trainer-plans" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager']}><TrainerPlans /></RequireRole>} />
              <Route path="trainers" element={<RequireGymModule moduleKey={GYM_MODULES.trainerStaff}><Trainers /></RequireGymModule>} />
              <Route path="yoga-content" element={<RequireDefaultGymAdmin><YogaContent /></RequireDefaultGymAdmin>} />
              <Route path="stress-content" element={<RequireDefaultGymAdmin><StressContent /></RequireDefaultGymAdmin>} />
              <Route path="goals" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager']}><Goals /></RequireRole>} />
              <Route path="notifications" element={<Navigate to="/dashboard/marketing" replace />} />
              <Route path="attendance" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','front_desk']}><Attendance /></RequireRole>} />
              <Route path="slot-management" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','front_desk']}><RequireGymModule moduleKey={GYM_MODULES.slotBooking}><SlotManagement /></RequireGymModule></RequireRole>} />
              <Route path="challenges-events" element={<RequireRole roles={['superadmin','gym_admin','admin']}><ChallengesEvents /></RequireRole>} />
              <Route path="database" element={<RequireRole roles={['superadmin']}><DatabaseManagement /></RequireRole>} />
              <Route path="feedback" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager','front_desk']}><Feedbacks /></RequireRole>} />
              <Route path="payments" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><Payments /></RequireRole>} />
              <Route path="gym-upi-payments" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><RequireGymModule moduleKey={GYM_MODULES.invoice}><GymUpiPayments /></RequireGymModule></RequireRole>} />
              <Route path="gym-invoices" element={<RequireRole roles={[ 'superadmin' ]}><GymInvoices /></RequireRole>} />
              <Route path="invoices" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><RequireGymModule moduleKey={GYM_MODULES.invoice}><Invoices /></RequireGymModule></RequireRole>} />
              <Route path="invoice-settings" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><RequireGymModule moduleKey={GYM_MODULES.invoice}><InvoiceSettings /></RequireGymModule></RequireRole>} />
              <Route path="gym-settings" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','front_desk']}><GymSettings /></RequireRole>} />
              <Route path="notification-templates" element={<RequireRole roles={['superadmin','gym_admin','admin']}><NotificationTemplates /></RequireRole>} />
              <Route path="integrations" element={<RequireRole roles={['superadmin','gym_admin','admin']}><Integrations /></RequireRole>} />
              <Route path="audit" element={<RequireRole roles={[ 'superadmin', 'admin' ]}><AuditLog /></RequireRole>} />
              <Route path="retention" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><RequireGymModule moduleKey={GYM_MODULES.memberRetention}><Retention /></RequireGymModule></RequireRole>} />
              <Route path="leads" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><Leads /></RequireRole>} />
              <Route path="social-operations" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><SocialOperations /></RequireRole>} />
              <Route path="biometric" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','front_desk']}><BiometricIntegration /></RequireRole>} />
              <Route path="pos" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff','front_desk']}><POS /></RequireRole>} />

            </Route>
        </Routes>
      </BrowserRouter>
      </AccessibilityProvider>
      </AppDataProvider>
    </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
